import { RefObject, useCallback, useEffect, useLayoutEffect, useState } from 'react';

/**
 * How many tiles fit on one row of a wrapping container, measured from the
 * rendered layout: the number of children sharing the first child's top edge.
 *
 * Measured, not computed, so it is right for any layout the container uses
 * (Send's overlapping flex row with negative margins, its 2- and 3-column phone
 * grids, Activity's fixed-width tiles) without knowing tile widths, gaps or
 * breakpoints. It needs one full row rendered to see the true count; with fewer
 * tiles than a row it returns that smaller count, which callers treat as
 * "everything fits".
 *
 * Returns null until the first measurement.
 */
export const useCardsPerRow = (
  containerRef: RefObject<HTMLElement | null>,
  /** Changes whenever the rendered tile set may have changed (count, density, size). */
  layoutKey: unknown,
): number | null => {
  const [perRow, setPerRow] = useState<number | null>(null);

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const tiles = Array.from(container.children) as HTMLElement[];
    if (tiles.length === 0) return;
    const firstTop = tiles[0].offsetTop;
    let count = 0;
    for (const tile of tiles) {
      if (tile.offsetTop !== firstTop) break;
      count += 1;
    }
    if (count > 0) setPerRow((prev) => (prev === count ? prev : count));
  }, [containerRef]);

  // After every relevant render, before paint, so a collapsed list never flashes the wrong count.
  useLayoutEffect(() => {
    measure();
  }, [measure, layoutKey]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => measure());
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef, measure]);

  return perRow;
};

export default useCardsPerRow;
